/**
 * GENERATED by scripts/sync-theme-css.mjs. Do not edit.
 *
 * Source: src/app/theme-contract.json (cuts section) + src/app/dcyfr-theme-obsidian.css
 * (dcyfr-labs/dcyfr-labs-registry@90aa17d06237).
 *
 * The cut axis of the obsidian identity: which `data-cut` values <html> may
 * carry, which one renders when the attribute is absent, a swatch per cut
 * for the chooser and the /design gallery, and the variables each cut
 * resolves to in each scheme. Each swatch is that cut's light-scheme
 * --accent-600 (the filled-control step) as the sRGB fallback the package
 * publishes, so it paints the same wherever it is used and does not depend
 * on which cut is currently stamped.
 *
 * cutVars holds, per cut and scheme, every variable the contract allows a
 * cut to re-author (cuts.overlayGroups plus cuts.overlayExtras), resolved
 * through the package's cascade the way <html> would compute them under
 * that stamp. The /design gallery applies a cut's set as inline custom
 * properties on one card, so six cuts render side by side while <html>
 * carries the visitor's own cut. Nothing in the gallery stamps
 * `data-cut`: the dark overlay selector is compound on <html>, so a stamp
 * on any other element would silently degrade to the base cut, and a card
 * that depended on the live stamp could show only one cut at a time.
 * Values are the sRGB main-context declarations; the @supports wide-gamut
 * overlays are not modelled here.
 *
 * Data only. Colour strings here are data for a swatch or a gallery card,
 * not a hardcoded colour in authored UI; the ESLint colour rule looks at
 * Tailwind class names, which this module has none of.
 */

export const themeCuts = {
  identity: 'obsidian',
  attribute: 'data-cut',
  base: 'black',
  cuts: ['black', 'red', 'blue', 'green', 'fire', 'rainbow'],
  swatches: {
    black: '#6b6c6e',
    red: '#9f5344',
    blue: '#5a6b91',
    green: '#5a745b',
    fire: '#906100',
    rainbow: '#586c92',
  },
} as const;

export type ThemeCut = (typeof themeCuts.cuts)[number];

export type CutScheme = 'light' | 'dark';

/** The variables a cut may re-author, in contract order. */
export const cutTokens = ['--primary', '--primary-foreground', '--accent', '--accent-foreground', '--accent-400', '--accent-500', '--accent-600', '--accent-700', '--field', '--field-foreground', '--field-accent', '--sheen', '--border', '--muted', '--muted-foreground'] as const;

export type CutToken = (typeof cutTokens)[number];

/** Per cut and scheme, what each of cutTokens resolves to under that stamp. */
export const cutVars = {
  black: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#b0b1b4',
      '--accent-500': '#919295',
      '--accent-600': '#6b6c6e',
      '--accent-700': '#545558',
      '--field': '#08090e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#816a52',
      '--sheen': 'none',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#b0b1b4',
      '--accent-500': '#919295',
      '--accent-600': '#6b6c6e',
      '--accent-700': '#545558',
      '--field': '#08090e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#816a52',
      '--sheen': 'none',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  red: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#eb9887',
      '--accent-500': '#c97a69',
      '--accent-600': '#9f5344',
      '--accent-700': '#853c2e',
      '--field': '#0e0807',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#eb9887',
      '--sheen': 'repeating-linear-gradient( 168deg, #0e0807 0 8px, #7a3b2f 8px 27px, #0e0807 27px 33px, #833425 33px 48px )',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#eb9887',
      '--accent-500': '#c97a69',
      '--accent-600': '#9f5344',
      '--accent-700': '#853c2e',
      '--field': '#0e0807',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#eb9887',
      '--sheen': 'repeating-linear-gradient( 168deg, #0e0807 0 8px, #7a3b2f 8px 27px, #0e0807 27px 33px, #833425 33px 48px )',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  blue: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#9eb1da',
      '--accent-500': '#8092ba',
      '--accent-600': '#5a6b91',
      '--accent-700': '#445479',
      '--field': '#080a0e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9eb1da',
      '--sheen': 'radial-gradient(farthest-side at 50% 50%, transparent 70%, #4c5a7959 100%)',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#9eb1da',
      '--accent-500': '#8092ba',
      '--accent-600': '#5a6b91',
      '--accent-700': '#445479',
      '--field': '#080a0e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9eb1da',
      '--sheen': 'radial-gradient(farthest-side at 50% 50%, transparent 70%, #4c5a7959 100%)',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  green: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#9eba9f',
      '--accent-500': '#809b81',
      '--accent-600': '#5a745b',
      '--accent-700': '#445d45',
      '--field': '#070b07',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9eba9f',
      '--sheen': 'linear-gradient( 115deg, transparent 36%, #5c51348c 46%, #bdb08c 50%, #5c51348c 54%, transparent 64% )',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#9eba9f',
      '--accent-500': '#809b81',
      '--accent-600': '#5a745b',
      '--accent-700': '#445d45',
      '--field': '#070b07',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9eba9f',
      '--sheen': 'linear-gradient( 115deg, transparent 36%, #5c51348c 46%, #bdb08c 50%, #5c51348c 54%, transparent 64% )',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  fire: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#e7a224',
      '--accent-500': '#c28400',
      '--accent-600': '#906100',
      '--accent-700': '#734d00',
      '--field': '#0c0905',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#e7a224',
      '--sheen': 'linear-gradient( 100deg, transparent 40%, #9f185f 45%, #b87d00 49%, #698c1a 53%, #5d3c9c 57%, transparent 62% )',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#e7a224',
      '--accent-500': '#c28400',
      '--accent-600': '#906100',
      '--accent-700': '#734d00',
      '--field': '#0c0905',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#e7a224',
      '--sheen': 'linear-gradient( 100deg, transparent 40%, #9f185f 45%, #b87d00 49%, #698c1a 53%, #5d3c9c 57%, transparent 62% )',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
  rainbow: {
    light: {
      '--primary': '#08090e',
      '--primary-foreground': '#f5efe9',
      '--accent': '#e9e2db',
      '--accent-foreground': '#08090e',
      '--accent-400': '#9cb2db',
      '--accent-500': '#7d92bb',
      '--accent-600': '#586c92',
      '--accent-700': '#42557a',
      '--field': '#080a0e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9cb2db',
      '--sheen': 'repeating-radial-gradient( ellipse 120% 80% at 50% 110%, #46597e 0 3.5px, #5b8d7e 3.5px 6.9px, #807441 6.9px 8.8px, transparent 8.8px 24px )',
      '--border': '#dacfc3',
      '--muted': '#e9e2db',
      '--muted-foreground': '#605a54',
    },
    dark: {
      '--primary': '#ebe7e4',
      '--primary-foreground': '#08090e',
      '--accent': '#1c1e23',
      '--accent-foreground': '#ebe7e4',
      '--accent-400': '#9cb2db',
      '--accent-500': '#7d92bb',
      '--accent-600': '#586c92',
      '--accent-700': '#42557a',
      '--field': '#080a0e',
      '--field-foreground': '#e2ddd8',
      '--field-accent': '#9cb2db',
      '--sheen': 'repeating-radial-gradient( ellipse 120% 80% at 50% 110%, #46597e 0 3.5px, #5b8d7e 3.5px 6.9px, #807441 6.9px 8.8px, transparent 8.8px 24px )',
      '--border': '#33291e',
      '--muted': '#1c1e23',
      '--muted-foreground': '#a39d98',
    },
  },
} as const satisfies Record<ThemeCut, Record<CutScheme, Record<CutToken, string>>>;

/** True for a declared cut name; anything else renders the base cut. */
export function isThemeCut(value: unknown): value is ThemeCut {
  return typeof value === 'string' && (themeCuts.cuts as readonly string[]).includes(value);
}
